/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * Tariff and services product saga
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.10.0
 */

import sortBy from 'lodash/sortBy';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';


import { IOptionItemProps } from '@components/common/Select/Select';
import { API_HOSTNAME, PRODUCTS_GET_SERVICES_AVAILABLE, PRODUCTS_GET_SERVICES_CONNECTED } from '@modules/app/Api';
import { EBasketType } from '@modules/app/appConstants';
import { getErrorTranslation } from '@modules/internationalization/helpers/intGetErrorTrnaslation';
import { makeSelectTranslationPageProducts } from '@modules/internationalization/intSelectors';
import { IPageProductsTranslation } from '@modules/internationalization/types/pages/products';
import { filterAvailableProducts } from '@pages/PageProducts/utils/filterAvailableProducts';

import { requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import {
    includeCampaignServiceProductsAction,
    includeCampaignTariffProductsAction,
    productsGetProductListAvailableFailureAction,
    productsGetProductListConnectedFailureAction,
    productsGetProductListSuccess,
    productsSetCategoryIdsAction,
} from './tariffsAndServicesActions';
import { EProductsPageFrom } from './tariffsAndServicesConstants';
import { IProductCardService, IProductCardServiceResponse } from './tariffsAndServicesProductServiceTypes';
import {
    ICardTariffNewItem,
    IProductCardTariff,
    IProductCardTariffResponse,
    IProductNewCardTariffResponse,
} from './tariffsAndServicesProductTariffTypes';
import {
    IProductCard,
    IProductCardResponse,
    IProductNewCard,
    IProductResponse,
} from './tariffsAndServicesProductTypes';
import {
    makeSelectProductsCategories,
    makeSelectProductsClientId,
    makeSelectProductsMeta,
    makeSelectProductsPageFrom,
    makeSelectProductsRegionSelected,
} from './tariffsAndServicesSelectors';
import { IProductsCategory, IProductsMeta } from './tariffsAndServicesTypes';
import { calculateOtherCategory } from './utils/calculateOtherCategory';
import { formatTariffCashback } from './utils/formatTariffCashback';
import { getRelatedCategoryIds } from './utils/getRelatedCategoryIds';

/*
 * -------------------------------------------
 *              API
 * -------------------------------------------
 */

export function* loadTariffsAndServicesProductsSaga(url: string): SagaIterator<IProductResponse> {
    try {
        const regionSelected: IOptionItemProps = yield select(makeSelectProductsRegionSelected());
        const params: { [key: string]: string | number } = { regionId: regionSelected.value };

        const pageFrom = yield select(makeSelectProductsPageFrom());
        const clientId = yield select(makeSelectProductsClientId());
        if (pageFrom === EProductsPageFrom.SUBSCRIBERS) {
            params.basketType = EBasketType.MOBILE;
        } else if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID && clientId) {
            params.subscriberId = clientId;
        }

        const queryString = formQueryParamsString(params);

        return yield call(requestSaga, `${url}${queryString}`, 'GET');
    } catch (e) {
        const productsTranslation: IPageProductsTranslation = yield select(makeSelectTranslationPageProducts());
        throw getErrorTranslation(e, productsTranslation.error);
    }
}

/*
 * -------------------------------------------
 *           Get product card
 * -------------------------------------------
 */

const urlAvailable = `${API_HOSTNAME}${PRODUCTS_GET_SERVICES_AVAILABLE}`;
const urlConnected = `${API_HOSTNAME}${PRODUCTS_GET_SERVICES_CONNECTED}`;

export function* getTariffsAndServicesListSaga(): SagaIterator {
    let dataAvailable: IProductResponse = { campaignServices: [], campaignTariffs: [], services: [], tariffs: [] };
    let dataConnected: IProductResponse = { services: [], tariffs: [] };

    const productsTranslation: IPageProductsTranslation = yield select(makeSelectTranslationPageProducts());

    try {
        // load available products
        try {
            dataAvailable = yield call(loadTariffsAndServicesProductsSaga, urlAvailable);
        } catch (errorMessage: any) {
            yield put(productsGetProductListAvailableFailureAction(errorMessage));
        }
        // load connected products
        try {
            dataConnected = yield call(loadTariffsAndServicesProductsSaga, urlConnected);
        } catch (errorMessage: any) {
            yield put(productsGetProductListConnectedFailureAction(errorMessage));
        }

        const filteredDataAvailable = filterAvailableProducts(dataAvailable);

        const productsMeta: IProductsMeta = yield select(makeSelectProductsMeta());
        const productsCategories: IProductsCategory[] = yield select(makeSelectProductsCategories());

        // add category id (other category) to the tariffs that has no matching category
        const processedDataAvailable = calculateOtherCategory(filteredDataAvailable, productsCategories, productsMeta);

        // get entity categories ids from productList
        yield put(productsSetCategoryIdsAction(getRelatedCategoryIds(processedDataAvailable)));

        const [productListAvailable, productListConnected] = [processedDataAvailable, dataConnected].map(
            (accum) => [
                ...sortBy(accum.services, 'weight').map((product: IProductCardServiceResponse) => ({
                    ...mapTariffsAndServicesProduct(product),
                    ...mapTariffsAndServicesProductToService(product),
                    isTariff: false,
                })),
                ...sortBy(accum.tariffs || [], 'weight').map(
                    (product: IProductCardTariffResponse | IProductNewCardTariffResponse) => {
                        const tariffProps =
                            mapTariffsToNewCardTariff(product as IProductNewCardTariffResponse, productsTranslation);

                        const productProps = mapTariffsAndServicesToNewCard(product);
                        return {
                            ...productProps,
                            ...tariffProps,
                            isTariff: true,
                        };
                    },
                ),
                // непубличные предложение
                ...sortBy(accum.campaignServices || [], 'weight').map((product: IProductCardServiceResponse) => ({
                    ...mapTariffsAndServicesProduct(product),
                    ...mapTariffsAndServicesProductToService(product),
                    categories: [productsMeta.campaignServicesCategoryId],
                    isTariff: false,
                    personalOfferingId: product.personalOfferingId,
                    limitConnections: product?.limitConnections,
                    connected: product?.connected,
                })),
                ...sortBy(accum.campaignTariffs || [], 'weight').map((product: IProductCardTariffResponse) => ({
                    ...mapTariffsAndServicesProduct(product),
                    ...mapTariffsAndServicesProductToTariff(product),
                    categories: [productsMeta.campaignTariffsCategoryId],
                    isTariff: true,
                    personalOfferingId: product.personalOfferingId,
                    limitConnections: product?.limitConnections,
                    connected: product?.connected,
                })),
            ],
        );

        yield put(
            includeCampaignServiceProductsAction({ isIncluded:  !!processedDataAvailable.campaignServices?.length }),
        );
        yield put(
            includeCampaignTariffProductsAction( { isIncluded:  !!processedDataAvailable.campaignTariffs?.length } ),
        );
        yield put(
            productsGetProductListSuccess({
                productListAvailable,
                productListConnected,
            }),
        );
    } catch (e) {
        const errorMessage = getErrorTranslation(e, productsTranslation.error);
        yield put(productsGetProductListAvailableFailureAction(errorMessage));
        yield put(productsGetProductListConnectedFailureAction(errorMessage));
    }
}

/*
 * -------------------------------------------
 *               Mapping
 * -------------------------------------------
 */

export const mapTariffsAndServicesProduct = (product: IProductCardResponse): Omit<IProductCard, 'isTariff'> => ({
    available: product.isAvailable,
    categories: product.groups,
    description: product.shortDescription,
    id: product.productOfferingId,
    mooreDetailsLink: product.descriptionRef || product.fileRef,
    title: product.title,
});

export const mapTariffsAndServicesToNewCard = (product: IProductCardResponse): Omit<IProductNewCard, 'isTariff'> => ({
    available: product.isAvailable,
    categories: product.groups,
    id: product.productOfferingId,
    descriptionRef: product.descriptionRef,
    fileRef: product.fileRef,
    title: product.title,
    shortDescription: product.shortDescription,
});

export const mapTariffsAndServicesProductToService = (
    product: IProductCardServiceResponse,
): Omit<IProductCardService, keyof IProductCard> => {
    const { productOfferingOneTimeCharges, productOfferingRecurringCharges } = product;

    return {
        productOfferingOneTimeCharges,
        productOfferingRecurringCharges,
    };
};

export const mapTariffsAndServicesProductToTariff = (
    product: IProductCardTariffResponse,
): Omit<IProductCardTariff, keyof IProductCard> => {
    const { benefits, cashback, internetLimited, internetUnlimited, payment, ranges, roamings } = product;
    // sorting
    if (roamings) roamings.value = sortBy(roamings.value, 'weight');

    return {
        benefits: benefits
            ? {
                  ...benefits,
                  value: sortBy(benefits.value, 'weight').map((item) => item.value),
              }
            : undefined,
        cashback,
        internetLimited: internetLimited
            ? {
                  ...internetLimited,
                  value: sortBy(internetLimited.value, 'weight').map((item) => item.value),
              }
            : undefined,
        internetUnlimited: internetUnlimited
            ? {
                  ...internetUnlimited,
                  value: sortBy(internetUnlimited.value, 'weight').map((item) => item.value),
              }
            : undefined,
        payment,
        ranges: sortBy(ranges, 'weight'),
        roamings,
    };
};

export const mapTariffsToNewCardTariff = (
    product: IProductNewCardTariffResponse,
    productsTranslation: IPageProductsTranslation,
): Omit<ICardTariffNewItem, keyof IProductCard> => {
    const {
        benefits,
        cashback,
        internetUnlimited,
        internetLimited,
        includedTariff,
        payment,
        ranges,
        roamings,
        megaPowers,
        badge,
        kollektiv,
        device,
        lineTitle,
        head,
        border,
    } = product;
    // sorting
    if (roamings) roamings.value = sortBy(roamings.value, 'weight');

    return {
        benefits: benefits
            ? {
                  ...benefits,
                  value: sortBy(benefits.value, 'weight'),
              }
            : undefined,
        internetUnlimited: internetUnlimited
            ? {
                  ...internetUnlimited,
                  value: sortBy(internetUnlimited.value, 'weight'),
              }
            : undefined,
        internetLimited: internetLimited
            ? {
                  ...internetLimited,
                  value: sortBy(internetLimited.value, 'weight'),
              }
            : undefined,
        includedTariff: includedTariff
            ? {
                  ...includedTariff,
                  value: sortBy(includedTariff.value, 'weight'),
              }
            : undefined,
        roamings: roamings
            ? {
                  ...roamings,
                  value: sortBy(roamings.value, 'weight'),
              }
            : undefined,
        formattedCashback: formatTariffCashback(productsTranslation, cashback, payment),
        megaPowers,
        payment,
        ranges: sortBy(ranges, 'weight'),
        badge,
        kollektiv: kollektiv,
        device: device,
        lineTitle,
        head,
        border,
    };
};
